import { useState, useEffect } from 'react';
import { adminStore, DEFAULT_LESSON_PLANS, type LessonPlan } from '../../../store/adminStore';

export default function TeacherEditor() {
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refresh = () => {
    setLessonPlans(adminStore.getStore().lessonPlans);
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  const handleDelete = (id: string) => {
    const updated = lessonPlans.filter((p) => p.id !== id);
    adminStore.updateLessonPlans(updated);
    setDeleteId(null);
    refresh();
  };

  const handleAdd = (plan: Omit<LessonPlan, 'id'>) => {
    const newPlan: LessonPlan = { ...plan, id: `plan-${Date.now()}` };
    adminStore.updateLessonPlans([...lessonPlans, newPlan]);
    setShowAddModal(false);
    refresh();
  };

  const handleReset = () => {
    adminStore.updateLessonPlans([...DEFAULT_LESSON_PLANS]);
    refresh();
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-bold text-gray-800">Kế Hoạch Bài Dạy (KHBD)</h2>
          <p className="text-xs text-gray-400">{lessonPlans.length} KHBD đang có</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Khôi phục mặc định
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Thêm KHBD
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {lessonPlans.map((plan) => (
          <div key={plan.id} className="bg-white rounded-2xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-orange-50 rounded-xl mt-0.5">
              <i className="ri-file-text-fill text-orange-500"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-bold text-sm text-gray-800">{plan.title}</h3>
                {plan.grade && (
                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                    {plan.grade}
                  </span>
                )}
                {plan.subject && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                    {plan.subject}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-1.5">
                {plan.author && `${plan.author} · `}
                {plan.duration}
              </p>
              {plan.description && (
                <p className="text-sm text-gray-500 line-clamp-2">{plan.description}</p>
              )}
              {plan.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {plan.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-gray-50 text-gray-400 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setDeleteId(plan.id)}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </div>
        ))}

        {lessonPlans.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-3">
              <i className="ri-file-text-line text-2xl text-gray-300"></i>
            </div>
            <p className="text-sm text-gray-400">Chưa có KHBD nào. Thêm mới hoặc khôi phục mặc định!</p>
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-sm w-full p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-2-fill text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Xóa KHBD?</h3>
            <p className="text-gray-400 text-sm mb-6">Hành động này không thể hoàn tác.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 cursor-pointer whitespace-nowrap"
              >
                Xóa ngay
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddLessonPlanModal onClose={() => setShowAddModal(false)} onAdd={handleAdd} />
      )}
    </div>
  );
}

function AddLessonPlanModal({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (plan: Omit<LessonPlan, 'id'>) => void;
}) {
  const [form, setForm] = useState({
    title: '',
    subject: '',
    duration: '45 phút',
    grade: '',
    author: '',
    description: '',
    tags: '',
  });
  const [saved, setSaved] = useState(false);

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    onAdd({
      title: form.title,
      subject: form.subject,
      duration: form.duration,
      grade: form.grade,
      author: form.author,
      description: form.description,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    });
    setSaved(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        {saved ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-fill text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Đã thêm KHBD!</h3>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800">Thêm KHBD mới</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-50 cursor-pointer"
              >
                <i className="ri-close-line text-gray-400"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Tiêu đề <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="VD: KHBD: Trò chơi Kéo co – Lớp 3"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Môn học</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="VD: Thể dục"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Thời lượng</label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Khối lớp</label>
                  <input
                    type="text"
                    value={form.grade}
                    onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
                    placeholder="VD: Lớp 3"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tác giả</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                    placeholder="VD: Cô Nguyễn Thị A"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mô tả</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Từ khóa
                  <span className="ml-1 font-normal text-gray-400 text-xs">(phân cách bằng dấu phẩy)</span>
                </label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder="thể dục, dân gian, văn hóa"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Thêm KHBD
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
